import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { debounceTime } from 'rxjs';
import { UserService, UserTagList } from '@app/user/user.service';
import { PageableComponent } from '@shared/pageable.component';
import { FitConfigProviderService } from '@shared/fit-config-provider.service';
import { ResolutionCenterService, ChatRoom } from '../resolution-center.service';
import { getLastMessageAttachmentPreview } from '../chat-attachment.util';
import { ChatRoomFormData } from './chat-room-modal/chat-room-modal.component';

@Component({
    selector: 'client-center-chat-rooms',
    templateUrl: './client-center-chat-rooms.component.html',
    styleUrls: ['./client-center-chat-rooms.component.scss'],
    standalone: false
})

export class ClientCenterChatRoomsComponent extends PageableComponent<any> implements OnInit, OnDestroy {
    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private userService: UserService,
        private fitConfigProviderService: FitConfigProviderService
    ) {
        super();

        this.searchQueryStream.pipe(
            debounceTime(500)
        ).subscribe(() => (
            this._page = 1,
            this.load()
        ));
    }

    get isCoach() { return this.fitConfigProviderService.isCoach; }

    public isLoading = true;
    public showLoadMoreRooms = false;
    public systemChatRooms: ChatRoom[] = [];
    public customChatRooms: ChatRoom[] = [];
    public roomStatus: '' | 'unread' | 'archived' | 'favorited' = '';
    public today = '';
    public yesterday = '';

    public userTagLists: UserTagList[] = [];
    
    // Modal state
    public showChatRoomModal = false;
    public editingChatRoom: ChatRoom | null = null;
    public step1FormData: ChatRoomFormData | null = null;
    public chatRoomModalInitialStep: 1 | 2 = 1;
    
    // System chat room modal state
    public showSystemChatRoomModal = false;
    public editingSystemChatRoom: ChatRoom | null = null;
    
    // Reset trigger to force messages-tab to reset even when room is already null
    public resetMessagesTabTrigger: number = 0;
    
    @Output() public showNewButton = new EventEmitter<boolean>();
    
    public searchChatRoom = new EventEmitter<string>();

    get selectedChatRoom() { return this._selectedChatRoom; }
    set selectedChatRoom(value: ChatRoom | null) {
        if (value !== this._selectedChatRoom) {
            this._selectedChatRoom = value;
        }
    }

    ngOnInit(): void {
        const now = new Date(Date.now());
        this.today = now.getFullYear() + '-' + (now.getMonth() + 1).toString().padStart(2, '0') + '-' + now.getDate().toString().padStart(2, '0');
        
        const yesterdayDate = new Date(now);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        this.yesterday = yesterdayDate.getFullYear() + '-' + (yesterdayDate.getMonth() + 1).toString().padStart(2, '0') + '-' + yesterdayDate.getDate().toString().padStart(2, '0');
        
        void Promise.all([
            this.loadSystemChatRooms(),
            this.loadChatRoomList()
        ]).finally(() => this.startChatRoomListPoll());
    }

    ngOnDestroy(): void {
        this.stopChatRoomListPoll();
    }

    async load() {
        this.loadChatRoomList();
    }

    async loadSystemChatRooms() {
        const { data, error } = await this.resolutionCenterService.getChatRoomList(1, 'system');
        if (error) {
            return;
        }

        this.systemChatRooms = data.data as ChatRoom[] | [];
    }

    async loadChatRoomList() {
        this.isLoading = true;
        
        const { data, error } = await this.resolutionCenterService.getChatRoomList(
            1, 
            'custom',
            this.roomStatus,
            this.searchQuery
        );
        
        if (error) {
            return;
        }

        this._list = data;
        this.customChatRooms = data.data as ChatRoom[] | [];
        this.showLoadMoreRooms = this.shouldShowLoadMoreButton(data.current_page, data.last_page);
        this.isLoading = false;
    }

    async loadMoreChatRooms() {
        this._roomPage++;
        this.showLoadMoreRooms = false;
        
        const { data, error } = await this.resolutionCenterService.getChatRoomList(
            this._roomPage, 
            'custom',
            this.searchQuery
        );
        
        // Placeholder for now
        setTimeout(() => {
            const incoming: ChatRoom[] = [];
            const merged = [...this.customChatRooms, ...incoming];
            this._list.data = merged;
            this.customChatRooms = merged;
            this.showLoadMoreRooms = false;
        }, 300);
    }

    onSelectedChatRoom(chatRoom: ChatRoom) {
        if (this.selectedChatRoom && chatRoom.id === this.selectedChatRoom.id) {
            return;
        }

        chatRoom.unread_count = 0;
        this.selectedChatRoom = chatRoom;
        this._page = 1;
        
        // TODO: Update notifications when API is ready
        // this.resolutionCenterService.updateTotalNotifications.next(true);
    }

    setRoomStatus(status: '' | 'unread' | 'archived' | 'favorited') {
        if (this.roomStatus === status) {
            return;
        }

        this.roomStatus = status;
        this._roomPage = 1;
        this._page = 1;
        this.loadChatRoomList();
    }

    async onRoomUpdated(updated: ChatRoom | null | undefined) {
        // Handle deletion: if updated is null/undefined, remove the selected chat room
        if (!updated) {
            if (this.selectedChatRoom?.id) {
                const deletedId = this.selectedChatRoom.id;
                // Remove from list
                this.customChatRooms = this.customChatRooms.filter(r => r.id !== deletedId);
                // Clear selection
                this.selectedChatRoom = null;
                // Reset messages tab
                this.resetMessagesTabTrigger++;
            }
            return;
        }

        if (!updated.id) {
            return;
        }

        const willRefreshList = this.roomStatus === 'favorited' || this.roomStatus === 'archived';

        if (willRefreshList) {
            // Clear selection first so the panel closes immediately (no star flash).
            // Then refresh the list.
            this.selectedChatRoom = null;
            this.resetMessagesTabTrigger++;
            await this.loadChatRoomList();
            return;
        }

        // Update selected room
        if (this.selectedChatRoom?.id === updated.id) {
            this.selectedChatRoom = { ...this.selectedChatRoom, ...updated };
        }

        // Update list item (custom or system depending on room type)
        if (updated.type === 'system') {
            const idx = this.systemChatRooms.findIndex(r => r.id === updated.id);
            if (idx !== -1) {
                const current = this.systemChatRooms[idx];
                const merged = { ...current, ...updated };
                this.systemChatRooms = [
                    ...this.systemChatRooms.slice(0, idx),
                    merged,
                    ...this.systemChatRooms.slice(idx + 1)
                ];
            }
        } else {
            const idx = this.customChatRooms.findIndex(r => r.id === updated.id);
            if (idx !== -1) {
                const current = this.customChatRooms[idx];
                const merged = { ...current, ...updated };
                this.customChatRooms = [
                    ...this.customChatRooms.slice(0, idx),
                    merged,
                    ...this.customChatRooms.slice(idx + 1)
                ];
            }
        }
    }

    onMessageSent(shouldReload: boolean) {
        if (!shouldReload) {
            return;
        }

        this._roomPage = 1;
        this._page = 1;
        this.loadChatRoomList();
        this.loadSystemChatRooms();
    }

    onRoomCreated(shouldReload: boolean) {
        if (!shouldReload) {
            return;
        }

        this._roomPage = 1;
        this._page = 1;
        this.loadChatRoomList();
    }

    onNewRoomButtonClick() {
        if (this.userTagLists.length === 0) {
            this.loadUserTagLists();
        }

        this.editingChatRoom = null;
        this.chatRoomModalInitialStep = 1;
        this.showChatRoomModal = true;
    }

    onEditCustomChatRoom(chatRoom: ChatRoom) {
        if (this.userTagLists.length === 0) {
            this.loadUserTagLists();
        }

        this.editingChatRoom = chatRoom;
        this.chatRoomModalInitialStep = 1;
        this.showChatRoomModal = true;
    }

    onAddMembersToChatRoom(chatRoom: ChatRoom) {
        if (this.userTagLists.length === 0) {
            this.loadUserTagLists();
        }

        this.editingChatRoom = chatRoom;
        this.chatRoomModalInitialStep = 2;
        this.showChatRoomModal = true;
    }

    onEditSystemChatRoom(chatRoom: ChatRoom) {
        this.editingSystemChatRoom = chatRoom;
        this.showSystemChatRoomModal = true;
    }

    onCloseSystemChatRoomModal() {
        this.showSystemChatRoomModal = false;
        this.editingSystemChatRoom = null;
    }

    onSystemChatRoomUpdated(chatRoom: ChatRoom) {
        // Update the chat room in the list
        const index = this.systemChatRooms.findIndex(r => r.id === chatRoom.id);
        if (index !== -1) {
            this.systemChatRooms[index] = chatRoom;
        }
        
        // Update selected room if it's the one being edited
        if (this.selectedChatRoom?.id === chatRoom.id) {
            this.selectedChatRoom = chatRoom;
        }
        
        // Close modal
        this.onCloseSystemChatRoomModal();
    }

    onCloseChatRoomModal() {
        this.showChatRoomModal = false;
        this.editingChatRoom = null;
        this.step1FormData = null;
        this.chatRoomModalInitialStep = 1;
    }

    onNextStep(formData: ChatRoomFormData) {
        // Store step 1 data and proceed to step 2
        this.step1FormData = formData;
    }

    onChatRoomCreated(chatRoom: ChatRoom) {
        // Add the new chat room to the list (at the top)
        this.customChatRooms = [chatRoom, ...this.customChatRooms];
        
        // Select the newly created chat room
        this.selectedChatRoom = chatRoom;
        
        // Reload the list to ensure it's up to date
        this.loadChatRoomList();
    }

    onChatRoomUpdated(chatRoom: ChatRoom) {
        // Update the chat room in the list
        const index = this.customChatRooms.findIndex(r => r.id === chatRoom.id);
        if (index !== -1) {
            this.customChatRooms[index] = chatRoom;
        }
        
        // Update selected room if it's the one being edited
        if (this.selectedChatRoom?.id === chatRoom.id) {
            this.selectedChatRoom = chatRoom;
        }
    }

    getRoomInitials(room: ChatRoom): string {
        if (!room.name) return '?';
        const words = room.name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    getLastMessage(chatRoom: ChatRoom): string {
        return getLastMessageAttachmentPreview(chatRoom.last_message, 'group').text;
    }

    getLastMessageWithAuthor(chatRoom: ChatRoom): string {
        const preview = getLastMessageAttachmentPreview(chatRoom.last_message, 'group');

        if (!chatRoom.last_message) {
            return '';
        }

        const userName = chatRoom.last_message.author.name;
        const message = chatRoom.last_message.message || '';

        if (message) {
            return userName ? `${userName}: ${message}` : message;
        }

        if ((chatRoom.last_message.attachments?.length ?? 0) > 0) {
            const senderName = chatRoom.last_message.is_my_message ? 'You' : chatRoom.last_message.author.name;
            const count = chatRoom.last_message.attachments.length;

            return count === 1
                ? `${senderName}: sent a ${preview.text}`
                : `${senderName}: sent ${preview.text}`;
        }

        return '';
    }

    clearSearch() {
        this.searchQuery = '';
        this.searchQueryStream.next('');
        this._roomPage = 1;
        this._page = 1;
        this.loadChatRoomList();
    }

    private loadUserTagLists() {
        this.userService.getUserTagLists().then((response) => {
            if (!response.error && response.data) {
                this.userTagLists = response.data.map(tagList => ({
                    ...tagList,
                    tags: Array.isArray(tagList.tags) ? tagList.tags : []
                }));
            }
        });
    }

    private shouldShowLoadMoreButton(currentPage: number, lastPage: number) {
        return currentPage < lastPage;
    }

    /** Light poll for list updates until WebSockets are implemented. */
    private chatRoomListPollIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly CHAT_ROOM_LIST_POLL_INTERVAL_MS = 30_000;
    private chatRoomListPollInFlight = false;

    private startChatRoomListPoll(): void {
        this.stopChatRoomListPoll();
        this.chatRoomListPollIntervalId = setInterval(() => {
            void this.refreshChatRoomList();
        }, this.CHAT_ROOM_LIST_POLL_INTERVAL_MS);
    }

    private stopChatRoomListPoll(): void {
        if (this.chatRoomListPollIntervalId != null) {
            clearInterval(this.chatRoomListPollIntervalId);
            this.chatRoomListPollIntervalId = null;
        }
    }

    /** Re-fetch loaded pages without the loading spinner so last message / unread counts stay current. */
    private async refreshChatRoomList(): Promise<void> {
        if (this.isLoading || this.chatRoomListPollInFlight) {
            return;
        }

        this.chatRoomListPollInFlight = true;
        const pagesToRefresh = Math.max(1, this._roomPage);
        const selectedRoomId = this.selectedChatRoom?.id;
        const selectedUnreadCount = this.selectedChatRoom?.unread_count ?? 0;

        try {
            const { data: systemData, error: systemError } = await this.resolutionCenterService.getChatRoomList(1, 'system');
            if (!systemError && systemData) {
                this.systemChatRooms = (systemData.data as unknown as ChatRoom[]) || [];
            }

            const merged: ChatRoom[] = [];
            let lastPage = 1;

            for (let page = 1; page <= pagesToRefresh; page++) {
                const { data, error } = await this.resolutionCenterService.getChatRoomList(
                    page, 'custom', this.roomStatus, this.searchQuery
                );
                if (error || !data) {
                    return;
                }

                merged.push(...((data.data as unknown as ChatRoom[]) || []));
                lastPage = data.last_page;
            }

            const seen = new Set<number>();
            this.customChatRooms = merged.filter(room => {
                if (seen.has(room.id)) {
                    return false;
                }
                seen.add(room.id);
                return true;
            });
            this.showLoadMoreRooms = lastPage > this._roomPage;

            if (selectedRoomId) {
                const refreshed =
                    this.systemChatRooms.find(r => r.id === selectedRoomId)
                    ?? this.customChatRooms.find(r => r.id === selectedRoomId);
                if (refreshed) {
                    this.selectedChatRoom = { ...refreshed, unread_count: selectedUnreadCount };
                }
            }
        } finally {
            this.chatRoomListPollInFlight = false;
        }
    }

    private _selectedChatRoom: ChatRoom | null = null;
    private _roomPage = 1;
}
